import { useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { collection, onSnapshot, query, where } from '@firebase/firestore';

import type { UserType } from '@shared/auth/index.js';
import type { IntakeStatusNote } from '@shared/patient-intake-status';

import { db } from '../../../../firebase-config.js';
import { useAuth } from '../../../../hooks/index.js';

import { determineIfUserCanSeeNote } from '../utils';

const buildWhereClause = ({ userType }: { userType: UserType | '' }) => {
  if (userType === 'patient') {
    return [where('visibility.isVisibleToPatient', '==', true)];
  }

  if (userType === 'support') {
    return [where('visibility.isMedicalNote', '==', false)];
  }

  return [];
};

type UseGetNoteParams = {
  patientId: string;
  stepId: string | null;
  noteConfig: Pick<IntakeStatusNote, 'id' | 'visibility'> | null;
};

export const useGetNote = ({ patientId, stepId, noteConfig }: UseGetNoteParams) => {
  const { userType } = useAuth();
  const [note, setNote] = useState<IntakeStatusNote | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!noteConfig || !stepId || !determineIfUserCanSeeNote({ userType, visibility: noteConfig.visibility })) {
      setNote(null);
      setIsLoading(false);

      return;
    }

    setIsLoading(true);

    const intakeStatusNoteQuery = query(
      collection(db, 'patients', patientId, 'intakeStatus', stepId, 'intakeStatusNotes'),
      where('__name__', '==', noteConfig.id),
      ...buildWhereClause({ userType }),
    );

    const unsubscribe = onSnapshot(
      intakeStatusNoteQuery,
      (snapshot) => {
        const firstDoc = snapshot.docs[0];
        const note = firstDoc?.data() as Omit<IntakeStatusNote, 'id'> | undefined;

        if (!firstDoc || !note) {
          setNote(null);
          setIsLoading(false);

          return;
        }

        setNote({ ...note, id: firstDoc.id });

        setIsLoading(false);
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error('Error fetching note:', {
            error,
            stepId,
            userType,
            patientId,
            noteConfig,
            intakeStatusNoteQuery,
          });
        }

        captureException(error, {
          tags: { issueIn: 'useGetNote' },
          extra: { patientId, stepId, noteConfig },
        });

        setError(error);
        setIsLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [noteConfig, patientId, stepId, userType]);

  return {
    note,
    isLoading,
    error,
  } as const;
};
